<script setup lang="ts">
/**
 * Aura AGM (MULTIPLE shareholders) — 문서 패키지 (Stage 스테퍼).
 *   Stage 1 Annual Return Letter · Stage 2 AGM Documents(multiple shareholders) ·
 *   Stage 3 Directors' Declaration (DDF, 이사별) · Stage 4 Notice for Controller (개인, 조건부) ·
 *   Stage 5 Notice for Controller (법인, 조건부).
 * Letter/DDF/개인 Notice 는 형제 `agm` 모듈 재사용. AGM Documents 와 법인 Notice 만 multi 전용.
 */
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from '../agm/cdd/DocScope.vue';
import LetterOfARDoc from '../agm/docs/LetterOfARDoc.vue';
import DdfDoc from '../agm/docs/DdfDoc.vue';
import NoticeControllerDoc from '../agm/docs/NoticeControllerDoc.vue';
import AgmDocumentsMultiDoc from './docs/AgmDocumentsMultiDoc.vue';
import NoticeControllerCorpDoc from './docs/NoticeControllerCorpDoc.vue';

type ListKey = 'ddf' | 'notice' | 'corpnotice';
type Slot =
  | { kind: 'fixed'; icon: string; name: string; comp: Component; key: string }
  | { kind: 'repeat'; icon: string; name: string; comp: Component; scope: string; id: number; listKey: ListKey };

interface StageDef { no: number; en: string; short: string; tools?: { key: ListKey; label: string }[]; buildSlots: () => Slot[]; }

const props = defineProps<{
  /** 자동채움 결과(이사 수 → DDF, 개인/법인 컨트롤러 수 → Notice)를 반응형으로 반영 */
  autoLocal?: { ddfIds?: number[]; noticeIds?: number[]; corpNoticeIds?: number[] } | null;
}>();

const ddfIds = ref<number[]>([1]);
const noticeIds = ref<number[]>([]);
const corpNoticeIds = ref<number[]>([]);
let ddfSeq = 1;
let noticeSeq = 0;
let corpNoticeSeq = 0;

watch(() => props.autoLocal, (v) => {
  if (!v) return;
  if (Array.isArray(v.ddfIds) && v.ddfIds.length) { ddfIds.value = [...v.ddfIds]; ddfSeq = Math.max(...v.ddfIds); }
  if (Array.isArray(v.noticeIds)) { noticeIds.value = [...v.noticeIds]; noticeSeq = v.noticeIds.length ? Math.max(...v.noticeIds) : 0; }
  if (Array.isArray(v.corpNoticeIds)) { corpNoticeIds.value = [...v.corpNoticeIds]; corpNoticeSeq = v.corpNoticeIds.length ? Math.max(...v.corpNoticeIds) : 0; }
}, { deep: true, immediate: true });

function add(key: ListKey) {
  if (key === 'ddf') { ddfSeq += 1; ddfIds.value.push(ddfSeq); }
  else if (key === 'notice') { noticeSeq += 1; noticeIds.value.push(noticeSeq); }
  else { corpNoticeSeq += 1; corpNoticeIds.value.push(corpNoticeSeq); }
}
function remove(key: ListKey, id: number) {
  if (key === 'ddf') { ddfIds.value = ddfIds.value.filter((x) => x !== id); if (!ddfIds.value.length) { ddfIds.value = [1]; ddfSeq = 1; } }
  else if (key === 'notice') noticeIds.value = noticeIds.value.filter((x) => x !== id);
  else corpNoticeIds.value = corpNoticeIds.value.filter((x) => x !== id);
}

const activeStages = computed((): StageDef[] => {
  const stages: Omit<StageDef, 'no'>[] = [
    {
      en: 'Annual Return Letter', short: 'AR Letter',
      buildSlots: () => [{ kind: 'fixed', key: 'letter', icon: '✉️', name: 'Letter of Notification & Lodgement of AR', comp: LetterOfARDoc }],
    },
    {
      en: 'AGM Documents (Multiple Shareholders)', short: 'AGM Docs',
      buildSlots: () => [{ kind: 'fixed', key: 'agm', icon: '📋', name: 'AGM Documents (DRW / Notice / Consent / Minutes / Attendance / Statements)', comp: AgmDocumentsMultiDoc }],
    },
    {
      en: "Directors' Declaration (DDF)", short: 'DDF',
      tools: [{ key: 'ddf', label: '+ Add DDF (per director)' }],
      buildSlots: () => ddfIds.value.map((id) => ({ kind: 'repeat' as const, icon: '📑', name: 'DDF (Disclosure of Interest)', comp: DdfDoc, scope: `ddf_${id}/`, id, listKey: 'ddf' as ListKey })),
    },
    {
      // 개인/법인 Notice 를 하나의 스테이지로. CATEGORY_CD(개인 B09002 / 법인 B09001)에 따라
      //   autofill 이 ctrl_(개인) / corpctrl_(법인) 으로 나눠 생성한다. 같은 "Controller Notice" 업무라 한 단계.
      en: 'Notice for Controller (conditional)', short: 'Controller',
      tools: [
        { key: 'notice', label: '+ Add Notice (Individual)' },
        { key: 'corpnotice', label: '+ Add Notice (Corporate)' },
      ],
      buildSlots: () => [
        ...noticeIds.value.map((id) => ({ kind: 'repeat' as const, icon: '🕵️', name: 'Notice for Controller — Change in Particulars (Individual)', comp: NoticeControllerDoc, scope: `ctrl_${id}/`, id, listKey: 'notice' as ListKey })),
        ...corpNoticeIds.value.map((id) => ({ kind: 'repeat' as const, icon: '🏢', name: 'Notice for Controller — Change in Particulars (Corporate)', comp: NoticeControllerCorpDoc, scope: `corpctrl_${id}/`, id, listKey: 'corpnotice' as ListKey })),
      ],
    },
  ];
  return stages.map((st, i) => ({ ...st, no: i + 1 }));
});

const currentStage = ref(1);
const totalStages = computed(() => activeStages.value.length);
function go(n: number) { if (n >= 1 && n <= totalStages.value) currentStage.value = n; }
watch(totalStages, (n) => { if (currentStage.value > n) currentStage.value = Math.max(1, n); });

const view = computed(() => {
  let n = 0;
  return activeStages.value.map((st) => ({
    no: st.no, en: st.en, short: st.short, tools: st.tools || [],
    docs: st.buildSlots().map((d) => ({ ...d, no: (n += 1) })),
  }));
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));

/* zoom */
const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { ddfIds: [...ddfIds.value], noticeIds: [...noticeIds.value], corpNoticeIds: [...corpNoticeIds.value], currentStage: currentStage.value }; }
function restoreLocalState(s: { ddfIds?: number[]; noticeIds?: number[]; corpNoticeIds?: number[]; currentStage?: number } | null) {
  if (!s) return;
  if (Array.isArray(s.ddfIds) && s.ddfIds.length) { ddfIds.value = [...s.ddfIds]; ddfSeq = Math.max(...s.ddfIds); }
  if (Array.isArray(s.noticeIds)) { noticeIds.value = [...s.noticeIds]; noticeSeq = s.noticeIds.length ? Math.max(...s.noticeIds) : 0; }
  if (Array.isArray(s.corpNoticeIds)) { corpNoticeIds.value = [...s.corpNoticeIds]; corpNoticeSeq = s.corpNoticeIds.length ? Math.max(...s.corpNoticeIds) : 0; }
  if (s.currentStage && s.currentStage >= 1 && s.currentStage <= totalStages.value) currentStage.value = s.currentStage;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="agm-pkg">
    <div class="stepper">
      <button v-for="st in view" :key="st.no" type="button" class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }" @click="go(st.no)">
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage {{ st.no }}</span><span class="stage-t">{{ st.en }}</span></div>
        <div v-if="st.tools.length" class="agm-toolbar">
          <span class="agm-toolbar-lbl">Add document</span>
          <button v-for="tool in st.tools" :key="tool.key" type="button" class="agm-tool" @click="add(tool.key)">{{ tool.label }}</button>
        </div>
        <div v-if="!st.docs.length" class="agm-empty">Add only if applicable. Use <b>Add document</b> above.</div>
        <div v-for="d in st.docs" :key="d.kind === 'fixed' ? d.key : d.scope" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span><span class="nm">{{ d.name }}</span>
            <template v-if="d.kind === 'repeat'">
              <span class="copy">#{{ d.id }}</span>
              <button type="button" class="agm-rm" title="Remove copy" @click="remove(d.listKey, d.id)">×</button>
            </template>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame">
            <DocScope v-if="d.kind === 'repeat'" :scope="d.scope"><component :is="d.comp" /></DocScope>
            <component v-else :is="d.comp" />
          </div>
        </div>
      </section>
    </div>

    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.agm-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.agm-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.agm-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.agm-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; }
.agm-tool:hover { background: #eaf3ff; }
.pkg-doc { width: 100%; }
.pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.agm-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.agm-empty { width: 100%; margin: 8px 0 18px; padding: 16px 18px; border: 1px dashed #c5d0e0; border-radius: 12px; background: #f7f9fc; color: #5b6b82; font-size: 13px; box-sizing: border-box; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
